import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { GROUPS } from '../data/tournament';
import { advanceKnockoutPick } from '../utils/bracketUtils';
import GroupStage from '../components/GroupStage';
import ThirdPlacePicker from '../components/ThirdPlacePicker';
import KnockoutBracket from '../components/KnockoutBracket';

export default function Bracket() {
  const { leagueId } = useParams();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [picks, setPicks] = useState({ groups: {}, thirdPlaceAdvance: [], knockout: {} });
  const [actualResults, setActualResults] = useState({});
  const [tab, setTab] = useState('groups');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/leagues/${leagueId}`),
      api.get('/teams'),
      api.get(`/brackets/league/${leagueId}`),
      api.get('/brackets/results'),
    ]).then(([leagueRes, teamsRes, bracketRes, resultsRes]) => {
      setLeague(leagueRes.data.league);
      setTeams(teamsRes.data);
      setActualResults(resultsRes.data || {});
      if (bracketRes.data.picks && Object.keys(bracketRes.data.picks).length > 0) {
        setPicks(bracketRes.data.picks);
      } else {
        // Initialize empty group picks with default order
        const initGroups = {};
        for (const g of GROUPS) {
          const groupTeams = teamsRes.data.filter(t => t.group_name === g);
          initGroups[g] = groupTeams.map(t => t.id);
        }
        setPicks({ groups: initGroups, thirdPlaceAdvance: [], knockout: {} });
      }
    }).catch(err => {
      if (err.response?.status === 403) navigate('/');
    }).finally(() => setLoading(false));
  }, [leagueId, navigate]);

  const teamById = useCallback((id) => teams.find(t => t.id === id), [teams]);
  const teamsByGroup = useCallback((g) => teams.filter(t => t.group_name === g), [teams]);

  const updateGroupOrder = (group, orderedIds) => {
    setPicks(p => ({ ...p, groups: { ...p.groups, [group]: orderedIds } }));
  };

  const toggleThirdPlace = (groupLetter) => {
    setPicks(p => {
      const cur = p.thirdPlaceAdvance || [];
      if (cur.includes(groupLetter)) return { ...p, thirdPlaceAdvance: cur.filter(g => g !== groupLetter) };
      if (cur.length >= 8) return p;
      return { ...p, thirdPlaceAdvance: [...cur, groupLetter] };
    });
  };

  const updateKnockout = (matchId, teamId) => {
    setPicks(p => ({ ...p, knockout: advanceKnockoutPick(p.knockout || {}, matchId, teamId) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await api.post(`/brackets/league/${leagueId}`, { picks });
      setMsg(`Bracket saved! Score so far: ${res.data.score} pts`);
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const groupsComplete = GROUPS.every(g => (picks.groups[g] || []).length === 4);
  const thirdComplete = (picks.thirdPlaceAdvance || []).length === 8;
  const r32Count = Object.keys(picks.knockout).filter(k => k.startsWith('r32')).length;

  if (loading) return <div className="loading">Loading bracket…</div>;
  if (!league) return <div className="loading">League not found.</div>;

  return (
    <div className="bracket-page">
      <div className="flex-between mb-2">
        <div>
          <h1>{league.name}</h1>
          <p className="sub">Fill out your predictions before the tournament starts on June 11, 2026</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>← Back</button>
      </div>

      {league.is_locked && (
        <div className="bracket-locked-banner">
          🔒 {league.lock_message || 'This league is locked — no more changes allowed. Good luck!'}
        </div>
      )}

      <div className="bracket-tabs">
        <button className={`bracket-tab ${tab === 'groups' ? 'active' : ''}`} onClick={() => setTab('groups')}>
          Group Stage {groupsComplete ? '✓' : ''}
        </button>
        <button className={`bracket-tab ${tab === 'third' ? 'active' : ''}`} onClick={() => setTab('third')}>
          3rd Place Picks {thirdComplete ? '✓' : `(${(picks.thirdPlaceAdvance||[]).length}/8)`}
        </button>
        <button className={`bracket-tab ${tab === 'knockout' ? 'active' : ''}`} onClick={() => setTab('knockout')}>
          Knockout Bracket {r32Count > 0 ? `(${r32Count}/16 R32)` : ''}
        </button>
      </div>

      {tab === 'groups' && (
        <GroupStage
          groups={GROUPS}
          teamsByGroup={teamsByGroup}
          picks={picks.groups}
          onUpdate={updateGroupOrder}
          locked={league.is_locked}
        />
      )}

      {tab === 'third' && (
        <ThirdPlacePicker
          groups={GROUPS}
          picks={picks}
          teamsByGroup={teamsByGroup}
          selected={picks.thirdPlaceAdvance || []}
          onToggle={toggleThirdPlace}
          locked={league.is_locked}
        />
      )}

      {tab === 'knockout' && (
        <KnockoutBracket
          picks={picks}
          teams={teams}
          knockout={picks.knockout || {}}
          onPick={updateKnockout}
          locked={league.is_locked}
          actualResults={actualResults}
        />
      )}

      {!league.is_locked && (
        <div className="save-bar">
          <div className="progress">
            <strong>Groups:</strong> {groupsComplete ? '✓ done' : 'in progress'} &nbsp;·&nbsp;
            <strong>3rd Place:</strong> {(picks.thirdPlaceAdvance||[]).length}/8 &nbsp;·&nbsp;
            <strong>R32:</strong> {r32Count}/16
          </div>
          <div className="flex-gap">
            {msg && <span style={{ color: msg.includes('saved') ? 'var(--success)' : 'var(--red)', fontSize: '0.85rem' }}>{msg}</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Bracket'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
